import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types – mirror the backend SlashCommand schema
// ---------------------------------------------------------------------------

export interface SlashCommandDto {
  id: string;
  created_at: string;
  modified_at: string;
  name: string;
  description: string;
  prompt_text: string;
}

interface SlashCommandListResponse {
  commands: SlashCommandDto[];
}

interface CreateSlashCommandRequest {
  name: string;
  description?: string;
  prompt_text: string;
}

interface UpdateSlashCommandRequest {
  name?: string;
  description?: string;
  prompt_text?: string;
}

interface BulkImportRequest {
  commands: Array<{
    name: string;
    description?: string;
    prompt_text: string;
  }>;
}

interface BulkImportResponse {
  imported_count: number;
  skipped_count: number;
  commands: SlashCommandDto[];
}

// ---------------------------------------------------------------------------
// RTK Query API slice
// ---------------------------------------------------------------------------

export const slashCommandsApiClient = createApi({
  reducerPath: 'slashCommandsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['SlashCommands'] as const,
  endpoints: (builder) => ({
    listSlashCommands: builder.query<SlashCommandListResponse, void>({
      query: () => 'api/slash-commands/',
      providesTags: ['SlashCommands'],
    }),

    createSlashCommand: builder.mutation<
      SlashCommandDto,
      CreateSlashCommandRequest
    >({
      query: (body) => ({
        url: 'api/slash-commands/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SlashCommands'],
    }),

    updateSlashCommand: builder.mutation<
      SlashCommandDto,
      { id: string; data: UpdateSlashCommandRequest }
    >({
      query: ({ id, data }) => ({
        url: `api/slash-commands/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['SlashCommands'],
    }),

    deleteSlashCommand: builder.mutation<void, string>({
      query: (id) => ({
        url: `api/slash-commands/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SlashCommands'],
    }),

    bulkImportSlashCommands: builder.mutation<
      BulkImportResponse,
      BulkImportRequest
    >({
      query: (body) => ({
        url: 'api/slash-commands/bulk-import/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SlashCommands'],
    }),
  }),
});

export const {
  useListSlashCommandsQuery,
  useCreateSlashCommandMutation,
  useUpdateSlashCommandMutation,
  useDeleteSlashCommandMutation,
  useBulkImportSlashCommandsMutation,
} = slashCommandsApiClient;
